import { supabase } from './db-utils';
import { parse, stringify } from 'flatted';

// ─── Tournament Cache (in-memory) ───────────────────────────────
let cachedTournament = null;

/**
 * Get the active tournament (status = 'in_progress', fallback to most recent).
 */
export async function getActiveTournament() {
  if (cachedTournament) return cachedTournament;

  const { data, error } = await supabase
    .from('tournaments')
    .select('id, name, short_name, status, start_date, end_date')
    .eq('status', 'in_progress')
    .limit(1)
    .single();

  if (error || !data) {
    const { data: fallback } = await supabase
      .from('tournaments')
      .select('id, name, short_name, status, start_date, end_date')
      .order('start_date', { ascending: false })
      .limit(1)
      .single();

    cachedTournament = fallback;
    return fallback;
  }

  cachedTournament = data;
  return data;
}

export function clearTournamentCache() {
  cachedTournament = null;
}

// ─── Cache Helpers ──────────────────────────────────────────────

const DATA_HOLD = 21600000;                        // 6 hours — auto-refresh on page load after this
const MANUAL_REFRESH_COOLDOWN = 14400000;          // 4 hours — default manual refresh gate
const POST_MATCH_REFRESH_COOLDOWN = 900000;        // 15 minutes — cooldown while bot is writing scores
const POST_MATCH_WINDOW_MS = 7200000;              // 2 hours after a match ends — how long we expect scores to trickle in
const T20_MATCH_DURATION_MS = 14400000;            // 4 hours — T20 scheduled length + grace
const CACHE_VERSION = '11';                        // Bump to invalidate all caches after schema changes
const CACHE_KEYS = ['results', 'schedule', 'individual', 'progression', 'topscores', 'userDetail', 'recapVotes'];
const PAGE_SIZE = 1000;                            // PostgREST max rows per request

/**
 * Fetch all rows for a query, paginating past the PostgREST 1000-row cap.
 * `buildQuery` must return a fresh builder each call — Supabase query builders
 * cannot be reused after awaiting.
 */
async function fetchAllPages(buildQuery) {
  const all = [];
  let from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + PAGE_SIZE - 1);
    if (error) return { data: null, error };
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return { data: all, error: null };
}

function isCacheValid(key) {
  if (typeof window === 'undefined') return false;

  // Invalidate if cache version changed
  const storedVersion = localStorage.getItem('cache_version');
  if (storedVersion !== CACHE_VERSION) {
    clearAllCache();
    localStorage.setItem('cache_version', CACHE_VERSION);
    return false;
  }

  const ts = localStorage.getItem(`${key}_last_updated`);
  if (!ts) return false;

  return Date.now() - parseInt(ts) < DATA_HOLD;
}

function getCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? parse(raw) : null;
  } catch {
    return null;
  }
}

function setCache(key, data) {
  try {
    localStorage.setItem(key, stringify(data));
    localStorage.setItem(`${key}_last_updated`, Date.now().toString());
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cacheWritten', { detail: { key } }));
    }
  } catch (e) {
    console.warn('Cache write failed:', e);
  }
}

/**
 * Generic cached fetch. Checks localStorage first, fetches from Supabase on miss.
 */
async function cachedFetch(cacheKey, fetchFn) {
  if (isCacheValid(cacheKey)) {
    const cached = getCache(cacheKey);
    if (cached) return cached;
  }

  const data = await fetchFn();
  setCache(cacheKey, data);
  return data;
}

// ─── Data Fetchers ──────────────────────────────────────────────

/**
 * Leaderboard data.
 * Returns: [{ name, rank, playerkey, slackuid, imageurl, totalmatch,
 *            matchpoints, bonuspoints, totalpoints, loss, accuracy, last5, beststreak }]
 */
export async function getLeaderboard(tournamentId) {
  return cachedFetch('results', () => fetchLeaderboard(tournamentId));
}

async function fetchLeaderboard(tournamentId) {
  if (!supabase || !tournamentId) return [];

  const { data, error } = await supabase.rpc('get_leaderboard', {
    p_tournament_id: tournamentId,
  });
  if (error || !data) {
    console.error('Error fetching leaderboard:', JSON.stringify(error));
    return [];
  }

  return data.map(row => ({
    name: row.display_name,
    playerkey: row.player_id,
    slackuid: row.slack_user_id,
    imageurl: row.avatar_url,
    totalmatch: row.total_match,
    matchpoints: parseFloat(row.match_points),
    bonuspoints: parseFloat(row.bonus_points),
    totalpoints: parseFloat(row.total_points),
    loss: row.loss_count,
    // Preserve the "50.0" / "55.6" one-decimal string format the UI expects.
    accuracy: parseFloat(row.accuracy).toFixed(1),
    last5: row.last5,
    beststreak: row.best_streak,
    rank: row.rank.toString(),
    rankChange: row.rank_change,
  }));
}

/**
 * Schedule data.
 * Returns: [{ matchkey, matchnumber, matchname, team1_flag, team2_flag,
 *            team1_name, team2_name, result, date, day, time, venue, city, bonusquestion }]
 */
export async function getScheduleData(tournamentId) {
  return cachedFetch('schedule', () => fetchSchedule(tournamentId));
}

async function fetchSchedule(tournamentId) {
  if (!supabase || !tournamentId) return [];

  const { data, error } = await supabase
    .from('schedule')
    .select(`
      id, match_number, date_time, venue, city, status, winner_team_id, stage,
      team1:teams!schedule_team1_id_fkey (id, name, short_name),
      team2:teams!schedule_team2_id_fkey (id, name, short_name),
      winner:teams!schedule_winner_team_id_fkey (id, name, short_name),
      bonus_questions (question_text, option_1, option_2, option_3, correct_option)
    `)
    .eq('tournament_id', tournamentId)
    .order('match_number', { ascending: true });

  if (error || !data) {
    console.error('Error fetching schedule:', JSON.stringify(error));
    return [];
  }

  // Fetch scores to compute max match/bonus/revote points per match
  const { data: scores } = await fetchAllPages(() => supabase
    .from('scores')
    .select(`
      match_id, match_points, bonus_points, revote_points,
      schedule!inner (id, tournament_id)
    `)
    .eq('schedule.tournament_id', tournamentId));

  const winnerPointsMap = {};
  const bonusPointsMap = {};
  const revotePointsMap = {};
  if (scores) {
    for (const score of scores) {
      const mid = score.match_id;
      const mp = parseFloat(score.match_points || 0);
      const bp = parseFloat(score.bonus_points || 0);
      const rp = parseFloat(score.revote_points || 0);
      if (mp > 0 && (!winnerPointsMap[mid] || mp > winnerPointsMap[mid])) {
        winnerPointsMap[mid] = mp;
      }
      if (bp > 0 && (!bonusPointsMap[mid] || bp > bonusPointsMap[mid])) {
        bonusPointsMap[mid] = bp;
      }
      if (rp > 0 && (!revotePointsMap[mid] || rp > revotePointsMap[mid])) {
        revotePointsMap[mid] = rp;
      }
    }
  }

  return data.map((match) => {
    const dt = new Date(match.date_time);
    const dateStr = dt.toLocaleDateString('en-CA');
    const dayStr = dt.toLocaleDateString('en-US', { weekday: 'long' });
    const timeStr = dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    // bonus_questions can be an object (single) or array depending on Supabase relationship
    const bqRaw = match.bonus_questions;
    const bonus = Array.isArray(bqRaw) ? bqRaw[0] : bqRaw;
    let bonusQuestionStr = '{}';
    let bonusData = null;
    if (bonus) {
      const correctAnswer = bonus[`option_${bonus.correct_option}`] || '';
      bonusQuestionStr = JSON.stringify({ [bonus.question_text]: correctAnswer });
      bonusData = {
        question: bonus.question_text,
        options: [bonus.option_1, bonus.option_2, bonus.option_3].filter(Boolean),
        correctAnswer,
        correctOption: bonus.correct_option,
      };
    }

    return {
      matchkey: match.id,
      matchnumber: match.match_number.toString(),
      matchname: `${match.team1?.short_name?.toUpperCase()} vs ${match.team2?.short_name?.toUpperCase()}`,
      team1_flag: match.team1?.short_name?.toLowerCase(),
      team2_flag: match.team2?.short_name?.toLowerCase(),
      team1_name: match.team1?.name,
      team2_name: match.team2?.name,
      status: match.status || '',
      stage: match.stage || 'League',
      result: match.winner?.short_name?.toLowerCase() || '',
      date: dateStr,
      day: dayStr,
      time: timeStr,
      dateTimeMs: dt.getTime(),
      venue: match.venue || '',
      city: match.city || '',
      bonusquestion: bonusQuestionStr,
      bonusData,
      bonuspoints: '',
      matchpoints: '',
      losingmatchpoints: '',
      winnerPoints: winnerPointsMap[match.id] || 0,
      matchBonusPoints: bonusPointsMap[match.id] || 0,
      matchRevotePoints: revotePointsMap[match.id] || 0,
    };
  });
}

/**
 * Individual player stats.
 * Returns: [{ playerkey, ranking, slackuid, name, imageurl, matchpoints, bonuspoints,
 *            totalpoints, totalmatch, loss, accuracy, predictions, bonuspredictions,
 *            matchpointssequence }]
 */
export async function getIndividualStats(tournamentId) {
  return cachedFetch('individual', () => fetchIndividualStats(tournamentId));
}

async function fetchIndividualStats(tournamentId) {
  if (!supabase || !tournamentId) return [];

  const buildScoresQuery = () => supabase
    .from('scores')
    .select(`
      player_id, match_id, match_points, bonus_points, revote_points, total_points,
      players!inner (id, slack_user_id, display_name, avatar_url),
      schedule!inner (id, tournament_id, match_number)
    `)
    .eq('schedule.tournament_id', tournamentId)
    .order('schedule(match_number)', { ascending: true });

  const [{ data: scores, error: scoresError }, { count: abandonedCount }] = await Promise.all([
    fetchAllPages(buildScoresQuery),
    supabase
      .from('schedule')
      .select('id', { count: 'exact', head: true })
      .eq('tournament_id', tournamentId)
      .eq('status', 'abandoned'),
  ]);

  if (scoresError || !scores) {
    console.error('Error fetching individual stats:', JSON.stringify(scoresError));
    return [];
  }

  const abandoned = abandonedCount || 0;

  const { data: matchVotes } = await fetchAllPages(() => supabase
    .from('match_votes')
    .select(`
      player_id, match_id, chosen_team_id,
      teams!match_votes_chosen_team_id_fkey (short_name),
      schedule!inner (id, tournament_id)
    `)
    .eq('schedule.tournament_id', tournamentId));

  const { data: revotes } = await fetchAllPages(() => supabase
    .from('revotes')
    .select(`
      player_id, match_id, chosen_team_id,
      teams!revotes_chosen_team_id_fkey (short_name),
      schedule!inner (id, tournament_id)
    `)
    .eq('schedule.tournament_id', tournamentId));

  // bonus_votes has no direct FK to bonus_questions — join via match_id in JS
  const { data: bonusVotes } = await fetchAllPages(() => supabase
    .from('bonus_votes')
    .select(`
      player_id, match_id, chosen_option,
      schedule!inner (id, tournament_id)
    `)
    .eq('schedule.tournament_id', tournamentId));

  // Fetch bonus_questions separately to resolve chosen_option number → text
  const { data: bonusQuestions } = await supabase
    .from('bonus_questions')
    .select('match_id, option_1, option_2, option_3')
    .in('match_id', (bonusVotes || []).map(v => v.match_id));

  // Group by player
  const playerMap = {};
  for (const score of scores) {
    const pid = score.player_id;
    if (!playerMap[pid]) {
      playerMap[pid] = { player: score.players, scores: [] };
    }
    playerMap[pid].scores.push(score);
  }

  // Vote lookups
  const matchVoteLookup = {};
  if (matchVotes) {
    for (const v of matchVotes) {
      if (!matchVoteLookup[v.player_id]) matchVoteLookup[v.player_id] = {};
      matchVoteLookup[v.player_id][v.match_id] = v.teams?.short_name?.toLowerCase() || '';
    }
  }

  const revoteLookup = {};
  if (revotes) {
    for (const v of revotes) {
      if (!revoteLookup[v.player_id]) revoteLookup[v.player_id] = {};
      revoteLookup[v.player_id][v.match_id] = v.teams?.short_name?.toLowerCase() || '';
    }
  }

  // Build bonus question lookup by match_id
  const bqByMatch = {};
  if (bonusQuestions) {
    for (const bq of bonusQuestions) {
      bqByMatch[bq.match_id] = bq;
    }
  }

  const bonusVoteLookup = {};
  if (bonusVotes) {
    for (const v of bonusVotes) {
      if (!bonusVoteLookup[v.player_id]) bonusVoteLookup[v.player_id] = {};
      const bq = bqByMatch[v.match_id];
      bonusVoteLookup[v.player_id][v.match_id] = bq ? (bq[`option_${v.chosen_option}`] || '') : '';
    }
  }

  const individuals = Object.values(playerMap).map(({ player, scores: ps }) => {
    const scoredMatches = ps.length;
    const totalMatch = scoredMatches + abandoned;
    // matchPoints includes revote_points so that Won + Bonus = Total
    const matchPoints = ps.reduce((s, r) => s + parseFloat(r.match_points || 0) + parseFloat(r.revote_points || 0), 0);
    const bonusPoints = ps.reduce((s, r) => s + parseFloat(r.bonus_points || 0), 0);
    const totalPoints = ps.reduce((s, r) => s + parseFloat(r.total_points || 0), 0);
    const lossCount = ps.filter(r => parseFloat(r.match_points) < 0).length;
    const correctMatch = ps.filter(r => parseFloat(r.match_points) > 0).length;
    const correctBonus = ps.filter(r => parseFloat(r.bonus_points) > 0).length;
    // Accuracy excludes abandoned matches (no predictions possible)
    const accuracy = scoredMatches > 0
      ? ((correctMatch + correctBonus) / (scoredMatches * 2) * 100).toFixed(1)
      : '0.0';

    const last5 = ps.slice(-5).map(r => parseFloat(r.match_points) > 0 ? 'W' : 'L').join(',');

    let bestStreak = 0, streak = 0;
    for (const r of ps) {
      if (parseFloat(r.match_points) > 0) { streak++; bestStreak = Math.max(bestStreak, streak); }
      else { streak = 0; }
    }

    const predictions = {};
    const bonusPredictions = {};
    const revotePredictions = {};
    const matchPointsSequence = [];
    const scoresByMatch = {};

    for (const score of ps) {
      const mid = score.match_id;
      predictions[mid] = matchVoteLookup[player.id]?.[mid] || 'absent';
      bonusPredictions[mid] = bonusVoteLookup[player.id]?.[mid] || 'absent';
      const rv = revoteLookup[player.id]?.[mid];
      if (rv) revotePredictions[mid] = rv;
      const totalPts = parseFloat(score.total_points || 0);
      matchPointsSequence.push(totalPts.toString());
      scoresByMatch[mid] = {
        matchPts: parseFloat(score.match_points || 0) + parseFloat(score.revote_points || 0),
        bonusPts: parseFloat(score.bonus_points || 0),
        totalPts,
      };
    }

    return {
      playerkey: player.id,
      ranking: '0',
      slackuid: player.slack_user_id,
      name: player.display_name,
      imageurl: player.avatar_url,
      matchpoints: parseFloat(matchPoints.toFixed(2)),
      bonuspoints: parseFloat(bonusPoints.toFixed(2)),
      totalpoints: parseFloat(totalPoints.toFixed(2)),
      totalmatch: totalMatch,
      loss: lossCount,
      accuracy,
      last5,
      beststreak: bestStreak,
      triviapoints: parseFloat(bonusPoints.toFixed(2)),
      predictions: JSON.stringify(predictions),
      bonuspredictions: JSON.stringify(bonusPredictions),
      revotepredictions: JSON.stringify(revotePredictions),
      matchpointssequence: matchPointsSequence.join(','),
      scoresByMatch,
    };
  });

  individuals.sort((a, b) => b.totalpoints - a.totalpoints);
  individuals.forEach((e, i) => { e.ranking = (i + 1).toString(); });
  return individuals;
}

/**
 * Single-player detail for the user page. Scoped query (~match-count rows),
 * no 1000-row cap risk. Ranking + aggregate stats pulled from the cached
 * leaderboard; per-match predictions/bonus/revote/scores fetched directly.
 * Returns a userRow-shaped object, or null if the player isn't on the
 * leaderboard (no scores yet) or the tournament isn't loaded.
 */
export async function getUserDetail(tournamentId, playerId) {
  if (!tournamentId || !playerId) return null;

  const cacheKey = 'userDetail';
  if (isCacheValid(cacheKey)) {
    const cached = getCache(cacheKey);
    if (cached && cached[playerId]) return cached[playerId];
  }

  const detail = await fetchUserDetail(tournamentId, playerId);
  if (!detail) return null;

  const existing = getCache(cacheKey) || {};
  existing[playerId] = detail;
  setCache(cacheKey, existing);
  return detail;
}

async function fetchUserDetail(tournamentId, playerId) {
  // Leaderboard gives us rank + aggregate stats (cached across user pages).
  const leaderboard = await getLeaderboard(tournamentId);
  const base = (leaderboard || []).find(p => p.playerkey === playerId);
  if (!base) return null;

  // All four queries are scoped to one player — bounded by match count (~70).
  const [{ data: scores }, { data: matchVotes }, { data: revotes }, { data: bonusVotes }] =
    await Promise.all([
      supabase
        .from('scores')
        .select(`
          match_id, match_points, bonus_points, revote_points, total_points,
          schedule!inner (id, tournament_id, match_number)
        `)
        .eq('player_id', playerId)
        .eq('schedule.tournament_id', tournamentId)
        .order('schedule(match_number)', { ascending: true }),
      supabase
        .from('match_votes')
        .select(`
          match_id, chosen_team_id,
          teams!match_votes_chosen_team_id_fkey (short_name),
          schedule!inner (id, tournament_id)
        `)
        .eq('player_id', playerId)
        .eq('schedule.tournament_id', tournamentId),
      supabase
        .from('revotes')
        .select(`
          match_id, chosen_team_id,
          teams!revotes_chosen_team_id_fkey (short_name),
          schedule!inner (id, tournament_id)
        `)
        .eq('player_id', playerId)
        .eq('schedule.tournament_id', tournamentId),
      supabase
        .from('bonus_votes')
        .select(`
          match_id, chosen_option,
          schedule!inner (id, tournament_id)
        `)
        .eq('player_id', playerId)
        .eq('schedule.tournament_id', tournamentId),
    ]);

  const { data: bonusQuestions } = await supabase
    .from('bonus_questions')
    .select('match_id, option_1, option_2, option_3')
    .in('match_id', (bonusVotes || []).map(v => v.match_id));

  const matchVoteByMatch = {};
  for (const v of matchVotes || []) {
    matchVoteByMatch[v.match_id] = v.teams?.short_name?.toLowerCase() || '';
  }
  const revoteByMatch = {};
  for (const v of revotes || []) {
    revoteByMatch[v.match_id] = v.teams?.short_name?.toLowerCase() || '';
  }
  const bqByMatch = {};
  for (const bq of bonusQuestions || []) {
    bqByMatch[bq.match_id] = bq;
  }
  const bonusByMatch = {};
  for (const v of bonusVotes || []) {
    const bq = bqByMatch[v.match_id];
    bonusByMatch[v.match_id] = bq ? (bq[`option_${v.chosen_option}`] || '') : '';
  }

  const predictions = {};
  const bonusPredictions = {};
  const revotePredictions = {};
  const matchPointsSequence = [];
  const scoresByMatch = {};

  for (const s of scores || []) {
    const mid = s.match_id;
    predictions[mid] = matchVoteByMatch[mid] || 'absent';
    bonusPredictions[mid] = bonusByMatch[mid] || 'absent';
    if (revoteByMatch[mid]) revotePredictions[mid] = revoteByMatch[mid];
    const totalPts = parseFloat(s.total_points || 0);
    matchPointsSequence.push(totalPts.toString());
    scoresByMatch[mid] = {
      matchPts: parseFloat(s.match_points || 0) + parseFloat(s.revote_points || 0),
      bonusPts: parseFloat(s.bonus_points || 0),
      totalPts,
    };
  }

  return {
    ...base,
    ranking: base.rank,
    triviapoints: base.bonuspoints,
    predictions: JSON.stringify(predictions),
    bonuspredictions: JSON.stringify(bonusPredictions),
    revotepredictions: JSON.stringify(revotePredictions),
    matchpointssequence: matchPointsSequence.join(','),
    scoresByMatch,
  };
}

/**
 * Progression data.
 * Returns: [{ name, data: "0,1,3,5,8,12" }]
 */
export async function getProgression(tournamentId) {
  return cachedFetch('progression', () => fetchProgression(tournamentId));
}

async function fetchProgression(tournamentId) {
  if (!supabase || !tournamentId) return [];

  const { data, error } = await supabase.rpc('get_progression', {
    p_tournament_id: tournamentId,
  });

  if (error || !data) {
    console.error('Error fetching progression:', JSON.stringify(error));
    return [];
  }

  return data.map(row => ({
    name: row.display_name,
    data: row.progression.map(v => parseFloat(v).toFixed(2)).join(','),
  }));
}

/**
 * Top score per match.
 * Returns: { [match_id]: maxTotalPoints }
 */
export async function getTopScorePerMatch(tournamentId) {
  return cachedFetch('topscores', () => fetchTopScorePerMatch(tournamentId));
}

async function fetchTopScorePerMatch(tournamentId) {
  if (!supabase || !tournamentId) return {};

  const { data, error } = await supabase
    .from('top_score_per_match')
    .select('match_id, top_total')
    .eq('tournament_id', tournamentId);

  if (error || !data) {
    console.error('Error fetching top scores:', JSON.stringify(error));
    return {};
  }

  const topScores = {};
  for (const row of data) {
    topScores[row.match_id] = parseFloat(row.top_total);
  }
  return topScores;
}

/**
 * Recap vote tallies — per-player team-vote counts.
 * Returns: { votesByPlayer: { [playerId]: { [teamShort]: count } } }
 *
 * Used by the player-recap (/recap/[playerId]) to derive "team loyalty"
 * beats. Cached under 'recapVotes' with the standard 6hr TTL so
 * subsequent visits are instant.
 */
export async function getRecapVoteTallies(tournamentId) {
  return cachedFetch('recapVotes', () => fetchRecapVoteTallies(tournamentId));
}

async function fetchRecapVoteTallies(tournamentId) {
  if (!supabase || !tournamentId) return { votesByPlayer: {} };

  const { data: votes, error } = await fetchAllPages(() => supabase
    .from('match_votes')
    .select(`
      player_id,
      teams!match_votes_chosen_team_id_fkey (short_name),
      schedule!inner (id, tournament_id)
    `)
    .eq('schedule.tournament_id', tournamentId));

  if (error || !votes) {
    console.error('Error fetching recap vote tallies:', JSON.stringify(error));
    return { votesByPlayer: {} };
  }

  const votesByPlayer = {};
  for (const v of votes) {
    const team = v.teams?.short_name?.toLowerCase();
    if (!team) continue;
    if (!votesByPlayer[v.player_id]) votesByPlayer[v.player_id] = {};
    votesByPlayer[v.player_id][team] = (votesByPlayer[v.player_id][team] || 0) + 1;
  }
  return { votesByPlayer };
}

/**
 * Fetch fresh leaderboard + schedule, and only commit to cache on success.
 * Used by the SyncIndicator manual-refresh flow so that a failed network call
 * leaves the user's existing cache intact (instead of wiping it first and
 * being unable to restore). Other per-page caches (individual, progression,
 * topscores) are cleared so their mounted consumers refetch on the
 * cacheCleared event that follows.
 */
export async function refetchForSync(tournamentId) {
  if (!tournamentId) return;
  const [schedule, leaderboard] = await Promise.all([
    fetchSchedule(tournamentId),
    fetchLeaderboard(tournamentId),
  ]);
  clearAllCache();
  setCache('schedule', schedule);
  setCache('results', leaderboard);
}

/**
 * Clear all cached data from localStorage.
 */
export function clearAllCache() {
  if (typeof window === 'undefined') return;
  CACHE_KEYS.forEach(key => {
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_last_updated`);
  });
}

/**
 * Timestamp of the oldest cache entry — treated as "last time the browser synced".
 * Returns null if nothing is cached yet.
 */
export function getLastSyncTime() {
  if (typeof window === 'undefined') return null;
  let oldest = null;
  for (const key of CACHE_KEYS) {
    const ts = localStorage.getItem(`${key}_last_updated`);
    if (!ts) continue;
    const t = parseInt(ts);
    if (Number.isNaN(t)) continue;
    if (oldest === null || t < oldest) oldest = t;
  }
  return oldest;
}

/**
 * Timestamp (ms) of the most recent match whose scheduled end has already passed.
 * Used to detect the post-match window during which the scoring bot is
 * actively writing results and users reasonably want tighter refresh cadence.
 * Returns null if no matches are cached yet or none have ended.
 */
export function getMostRecentMatchEnd() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('schedule');
    if (!raw) return null;
    const schedule = parse(raw);
    if (!Array.isArray(schedule)) return null;
    const now = Date.now();
    let latestEnd = null;
    for (const m of schedule) {
      if (!m.dateTimeMs) continue;
      const endMs = m.dateTimeMs + T20_MATCH_DURATION_MS;
      if (endMs > now) continue;
      if (latestEnd === null || endMs > latestEnd) latestEnd = endMs;
    }
    return latestEnd;
  } catch {
    return null;
  }
}

/**
 * Currently-active manual refresh cooldown in ms.
 * Drops to POST_MATCH_REFRESH_COOLDOWN (15 min) while we're within 2 hours
 * of the most recent match end — scores are actively being written then.
 * Otherwise the default 4 hour cooldown applies.
 */
export function currentManualCooldownMs() {
  const mostRecentEnd = getMostRecentMatchEnd();
  if (mostRecentEnd != null && Date.now() - mostRecentEnd < POST_MATCH_WINDOW_MS) {
    return POST_MATCH_REFRESH_COOLDOWN;
  }
  return MANUAL_REFRESH_COOLDOWN;
}

/**
 * Whether the user can trigger a manual refresh.
 * Honours a short cooldown (15 min) during the post-match scoring window and
 * the default 4 hour cooldown otherwise. Returns false if no sync has happened
 * yet (initial load is in flight).
 */
export function canManualRefresh() {
  const ts = getLastSyncTime();
  if (!ts) return false;
  return Date.now() - ts >= currentManualCooldownMs();
}

export const MANUAL_REFRESH_COOLDOWN_MS = MANUAL_REFRESH_COOLDOWN;
export const POST_MATCH_REFRESH_COOLDOWN_MS = POST_MATCH_REFRESH_COOLDOWN;

/**
 * Latest match for which scores have been logged.
 * Reads the cached `schedule` row (populated by getScheduleData) and picks
 * the highest-numbered match with any winnerPoints > 0.
 * Returns the schedule row shape, or null if nothing is scored yet.
 */
export function getLatestScoredMatch() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('schedule');
    if (!raw) return null;
    const schedule = parse(raw);
    if (!Array.isArray(schedule) || schedule.length === 0) return null;
    const scored = schedule.filter(m => (m.winnerPoints || 0) > 0);
    if (scored.length === 0) return null;
    scored.sort((a, b) => parseInt(b.matchnumber) - parseInt(a.matchnumber));
    return scored[0];
  } catch {
    return null;
  }
}
